<template>
  <div class="qr-scanner-wrapper" v-if="scannerState !== 'not-supported'">
    <div
      class="qr-viewfinder"
      :class="{
        'border-success': scannerState === 'success',
        'border-warning': scannerState === 'failed',
        'border-danger': scannerState === 'permission-denied',
      }"
      aria-label="QR code scanner"
    >
      <video ref="video" class="qr-video" v-show="scannerState === 'scanning' || scannerState === 'initialising'"></video>

      <div v-if="scannerState === 'initialising'" class="qr-overlay text-center py-3">
        <b-spinner variant="primary" small />
        <p class="mt-2 mb-0 small">Starting camera...</p>
      </div>

      <div v-if="scannerState === 'failed'" class="qr-overlay text-center py-3">
        <p class="mb-2 text-warning">Can't read QR</p>
        <b-button variant="outline-warning" size="sm" @click="retryScanning">
          Try again
        </b-button>
      </div>

      <div v-if="scannerState === 'permission-denied'" class="qr-overlay text-center py-3">
        <p class="mb-0 text-danger">Camera not available</p>
        <p class="mb-0 small text-muted">Please use manual entry below</p>
      </div>
    </div>

    <a
      href="#"
      class="d-block text-center mt-2 small text-muted"
      @click.prevent="$emit('manual-entry-requested')"
    >
      Enter member number instead
    </a>
  </div>
</template>

<script>
import QrScanner from 'qr-scanner';

export default {
    name: 'QrScanner',
    data() {
        return {
            scanner: null,
            scannerState: 'initialising', // initialising | scanning | success | failed | permission-denied | not-supported
            scanTimeout: null,
        };
    },
    mounted() {
        if (!QrScanner.hasCamera) {
            QrScanner.hasCamera().then((hasCamera) => {
                if (!hasCamera) {
                    this.scannerState = 'not-supported';
                    return;
                }
                this.initScanner();
            });
        } else {
            this.initScanner();
        }
    },
    beforeDestroy() {
        if (this.scanTimeout) {
            clearTimeout(this.scanTimeout);
        }
        if (this.scanner) {
            this.scanner.destroy();
            this.scanner = null;
        }
    },
    methods: {
        initScanner() {
            this.$nextTick(() => {
                if (!this.$refs.video) return;

                this.scanner = new QrScanner(
                    this.$refs.video,
                    (result) => {
                        this.onScanSuccess(result.data || result);
                    },
                    {
                        returnDetailedScanResult: true,
                        highlightScanRegion: false,
                        highlightCodeOutline: false,
                    }
                );

                this.scanner.start().then(() => {
                    this.scannerState = 'scanning';

                    this.scanTimeout = setTimeout(() => {
                        if (this.scannerState === 'scanning') {
                            this.scannerState = 'failed';
                            this.$emit('scan-failed', 'timeout');
                        }
                    }, 15000);
                }).catch((err) => {
                    if (err.name === 'NotAllowedError') {
                        this.scannerState = 'permission-denied';
                    } else {
                        this.scannerState = 'not-supported';
                    }
                });
            });
        },
        onScanSuccess(data) {
            if (this.scanTimeout) {
                clearTimeout(this.scanTimeout);
            }
            this.scannerState = 'success';
            if (this.scanner) {
                this.scanner.stop();
            }
            this.$emit('scan-success', data);
        },
        retryScanning() {
            if (this.scanTimeout) {
                clearTimeout(this.scanTimeout);
            }
            this.scannerState = 'scanning';
            if (this.scanner) {
                this.scanner.start().then(() => {
                    this.scanTimeout = setTimeout(() => {
                        if (this.scannerState === 'scanning') {
                            this.scannerState = 'failed';
                            this.$emit('scan-failed', 'timeout');
                        }
                    }, 15000);
                }).catch(() => {
                    this.scannerState = 'failed';
                });
            } else {
                this.initScanner();
            }
        },
    },
};
</script>

<style scoped lang="scss">
.qr-scanner-wrapper {
    max-width: 280px;
    margin: 0 auto;
}

.qr-viewfinder {
    position: relative;
    border: 3px solid $brand-primary;
    border-radius: 8px;
    overflow: hidden;
    background: $surface-card;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.border-success {
        border-color: $feedback-success;
    }

    &.border-warning {
        border-color: $feedback-warning;
    }

    &.border-danger {
        border-color: $feedback-error;
    }
}

.qr-video {
    width: 100%;
    height: auto;
    display: block;
}

.qr-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba($surface-bg, 0.8);
}
</style>
